import { JSONSchema7Definition } from 'json-schema'
import papaparseLib from 'papaparse'
import type { ParseError } from 'papaparse'
// papaparse is CJS-only; pull named values from the default export.
const { parse: papaparse } = papaparseLib
import { useCallback, useMemo } from 'react'
import { isObject } from 'lodash-es'

export type ParseCsvOptions = {
  /* If provided, items will be parsed based on the data type prescribed by the schema */
  jsonSchemaDefinition?: JSONSchema7Definition
}

export type UseParseCsvReturn = {
  parse: (dataToParse: string) => Promise<unknown[]>
}

const DEFAULT_OPTIONS: ParseCsvOptions = {
  jsonSchemaDefinition: { type: 'string' },
}

export class UseParseCsvError extends Error {
  public parseErrors: ParseError[]
  constructor(parseErrors: ParseError[]) {
    super('Error parsing CSV')
    this.parseErrors = parseErrors
  }
}

export default function useParseCsv(
  options: ParseCsvOptions = DEFAULT_OPTIONS,
): UseParseCsvReturn {
  const { jsonSchemaDefinition } = options
  const itemsAreString = useMemo(
    () =>
      isObject(jsonSchemaDefinition) &&
      jsonSchemaDefinition.type &&
      jsonSchemaDefinition.type === 'string',
    [jsonSchemaDefinition],
  )

  const parse = useCallback(
    (dataToParse: string): Promise<unknown[]> => {
      return new Promise((resolve, reject) => {
        papaparse(dataToParse, {
          // If the items are not strings, let papaparse guess the type. Otherwise, always parse as strings.
          dynamicTyping: !itemsAreString,
          complete: result => {
            if (result.errors.length > 0) {
              reject(new UseParseCsvError(result.errors))
            } else {
              resolve(result.data.flat())
            }
          },
        })
      })
    },
    [itemsAreString],
  )

  return {
    parse,
  }
}
